import * as yup from 'yup'
import { isEmpty, isNull, isUndefined } from '../../../utils/fns'
import { decimal, integer, range } from '../../../utils/validation'
import { APPLICATION_TYPES, ROOF_MEMBER_TYPES } from './constants'

import { getHeelMaxHeight, getHeelMinHeight } from './fns'

function heelRange() {
  return this.test({
    name: 'heelRange',
    exclusive: true,
    test: function (value) {
      let min = 3.5
      let max = 40
      let message = ''
      const { path, createError, parent } = this

      switch (parent.roofMemberType) {
        case ROOF_MEMBER_TYPES.RAFTER:
          max = getHeelMaxHeight(parent.topChordDepth, parent.topSlope)
          message = `The ${max} value is Max Heel Height based on the roof geometry.`
          break
        case ROOF_MEMBER_TYPES.IJOIST:
          min = getHeelMinHeight(parent.topChordDepth, parent.thickness, parent.topSlope)
          max = getHeelMaxHeight(parent.topChordDepth, parent.topSlope)
          message = `The ${min} and ${max} values are Min and Max Heel Height based on the roof geometry.`
          break
        default:
          break
      }
      if (Number(value) < min || Number(value) > max) {
        return createError({
          path,
          message: `Please enter a value between ${min} and ${max}. 
          ${message}`,
        })
      }

      if (
        parent.roofMemberType === ROOF_MEMBER_TYPES.TRUSS &&
        Number(parent.topSlope) === 0 &&
        Number(value) > 3.5 &&
        Number(value) < parent.topChordDepth + parent.bottomChordDepth
      )
        return createError({
          path,
          message: `Overlapping chord members! Please adjust the Heel Height.`,
        })

      return true
    },
  })
}

function slopeRange(min, max) {
  return this.test({
    name: 'slopeRange',
    exclusive: true,
    test: function (value) {
      const { path, createError } = this
      if (value < min || value > max) {
        return createError({
          path,
          message: `Enter a slope between 0/12 and 12/12 or an angle between 0° and 45°.`,
        })
      }
      return true
    },
  })
}

function bottomSlopeMaxValue() {
  return this.test({
    name: 'isLargerThan',
    exclusive: true,
    test: function (value) {
      const { path, createError, parent } = this
      if (value > Number(parent.topSlope)) {
        return createError({
          path,
          message: 'The Bottom Chord Slope/Angle cannot be greater than the Top Chord Slope/Angle.',
        })
      }
      return true
    },
  })
}

function f2Integer() {
  return this.test({
    name: 'f2Integer',
    exclusive: true,
    test: function (value) {
      const { path, createError, parent } = this
      let fieldName =
        parent.applicationType === APPLICATION_TYPES.PARALLEL_TO_WALL ? 'F2 Toward' : 'F2'
      if (isNull(value) || isUndefined(value) || isEmpty(value)) {
        return createError({ path, message: `${fieldName} is a required field.` })
      }
      if (isNaN(value)) {
        return createError({ path, message: `${fieldName} field must be a number.` })
      }
      if (!String(value).match(/^-?\d+$/)) {
        return createError({
          path,
          message: `${fieldName} field only accepts whole numbers.`,
        })
      }
      return true
    },
  })
}

yup.addMethod(yup.mixed, 'decimal', decimal)
yup.addMethod(yup.mixed, 'integer', integer)
yup.addMethod(yup.mixed, 'range', range)
yup.addMethod(yup.mixed, 'slopeRange', slopeRange)
yup.addMethod(yup.mixed, 'heelRange', heelRange)
yup.addMethod(yup.mixed, 'bottomSlopeMaxValue', bottomSlopeMaxValue)
yup.addMethod(yup.mixed, 'f2Integer', f2Integer)

export const InputSchema = yup.object().shape({
  applicationType: yup.string().required(),
  memberType: yup.string().required(),
  roofMemberType: yup.string().required(),
  uplift: yup.mixed().integer('Uplift').range(0, 64000),
  download: yup.mixed().when('applicationType', {
    is: (val) => val === APPLICATION_TYPES.PERPENDICULAR_TO_WALL,
    then: yup.mixed().integer('Download').range(0, 64000),
    otherwise: yup.mixed().notRequired(),
  }),
  f1: yup.mixed().integer('F1').range(0, 64000),
  f2: yup.mixed().when('applicationType', {
    is: (val) => val !== APPLICATION_TYPES.ANGLE_TO_WALL,
    then: yup.mixed().f2Integer().range(0, 64000),
    otherwise: yup.mixed().notRequired(),
  }),
  f2Away: yup.mixed().when('applicationType', {
    is: (val) => val === APPLICATION_TYPES.PARALLEL_TO_WALL,
    then: yup.mixed().integer('F2 Away').range(0, 64000),
    otherwise: yup.mixed().notRequired(),
  }),
  wallLength: yup.mixed().when('applicationType', {
    is: (val) => val === APPLICATION_TYPES.PARALLEL_TO_WALL,
    then: yup
      .mixed()
      .decimal('Wall Length')
      .range(1, 1200, 'Enter a Wall Length between 1/12 ft (1 inch) and 100 ft (1200 inches).'),
    otherwise: yup.mixed().notRequired(),
  }),
  heel: yup.mixed().decimal('Heel Height').heelRange(),
  topSlope: yup.mixed().when('roofMemberType', {
    is: (val) => val === ROOF_MEMBER_TYPES.TRUSS,
    then: yup.mixed().decimal('Top Chord Slope/Angle').slopeRange(0, 12),
    otherwise: yup.mixed().decimal('Slope/Angle').slopeRange(0, 12),
  }),
  bottomSlope: yup.mixed().when('roofMemberType', {
    is: (val) => val === ROOF_MEMBER_TYPES.TRUSS,
    then: yup.mixed().decimal('Bottom Chord Slope/Angle').bottomSlopeMaxValue().slopeRange(0, 12),
    otherwise: yup.mixed().notRequired(),
  }),
  studAlignmentOffset: yup.mixed().when(['applicationType', 'memberType'], {
    is: (appType, wallType) =>
      appType === APPLICATION_TYPES.PERPENDICULAR_TO_WALL && wallType === 'stud',
    then: yup.mixed().decimal('Stud Alignment').range(-8, 8),
    otherwise: yup.mixed().notRequired(),
  }),
})
